/**
 * Défi SKILLCARD + classement « Mon cercle » (migration 0285).
 *
 * - `create_card_share(card)` fige prénom + maîtrise + HORA et renvoie un jeton
 *   opaque ; le lien partagé pointe vers la page MARKETING
 *   `/skillcards/defi/{token}` (image OG, l'app est une SPA) qui renvoie vers
 *   `/card/{id}?defi={token}`.
 * - `get_card_share(token)` (public) : bandeau « Philippe te défie : 80 % ».
 * - `accept_card_share(token)` : compte connecté → le partageur est prévenu.
 * - `get_card_circle_leaderboard(card)` : HORA + maîtrise des proches.
 *
 * Helpers PURS testés en bas de fichier (`challenge.test.ts`).
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

// Domaine canonique (l'apex redirige, et les robots des réseaux ne suivent pas toujours).
export const CHALLENGE_PUBLIC_ORIGIN = 'https://www.jeraisonne.fr';

export interface CardShare {
  token: string;
  first_name: string;
  mastery: number | null;
  hora: number | null;
  xp_max: number | null;
}

export interface PublicCardShare extends CardShare {
  card_id: string;
  short_code: string;
  title: string | null;
}

export type CircleRelation = 'moi' | 'prof' | 'parent' | 'enfant' | 'eleve' | 'camarade';

export interface CircleEntry {
  rank: number;
  first_name: string;
  avatar_url: string | null;
  relation: CircleRelation;
  hora: number;
  mastery: number;
  is_me: boolean;
}

export interface CircleLeaderboard {
  entries: CircleEntry[];
  me: CircleEntry | null;
  total: number;
  /** Ma progression est-elle visible de mes camarades ? */
  my_visibility: boolean;
}

// ── Helpers purs ────────────────────────────────────────────────────────────

const TOKEN_RE = /^[0-9a-f]{12}$/;

export function isChallengeToken(v: unknown): v is string {
  return typeof v === 'string' && TOKEN_RE.test(v);
}

/** Lien partagé (page marketing avec image OG). `origin` : site local en dev. */
export function buildChallengeUrl(token: string, origin?: string | null): string {
  const o = origin?.trim().replace(/\/+$/, '') || CHALLENGE_PUBLIC_ORIGIN;
  return `${o}/skillcards/defi/${token}`;
}

/** Fiche SEO d'une carte sur le site (image OG `og-card`) : `/skillcards/{slug}`. */
export function buildCardPageUrl(slug: string, origin?: string | null): string {
  const o = origin?.trim().replace(/\/+$/, '') || CHALLENGE_PUBLIC_ORIGIN;
  return `${o}/skillcards/${slug}`;
}

function ogFunctionUrl(fn: 'og-card' | 'og-card-challenge', param: string, value: string) {
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/$/, '');
  return base ? `${base}/functions/v1/${fn}?${param}=${encodeURIComponent(value)}` : null;
}

/**
 * Pré-génère l'aperçu d'un lien partagé (image OG + page) dès le clic, sans
 * l'attendre : le rendu tourne pendant que l'élève choisit son contact, et
 * WhatsApp & co trouvent une image déjà prête. Silencieux en cas d'échec.
 */
function warmUrls(urls: (string | null)[]): void {
  if (typeof fetch !== 'function') return;
  for (const url of urls) {
    if (!url) continue;
    try {
      void fetch(url, { mode: 'no-cors', keepalive: true }).catch(() => undefined);
    } catch {
      /* aperçu facultatif */
    }
  }
}

export function warmChallengePreview(token: string, challengeUrl: string): void {
  warmUrls([ogFunctionUrl('og-card-challenge', 'token', token), challengeUrl]);
}

export function warmCardPreview(cardId: string, pageUrl: string): void {
  warmUrls([ogFunctionUrl('og-card', 'card', cardId), pageUrl]);
}

/** Slug SEO d'une carte publiée (migration 0298), chargé avec la page pour un partage sans attente. */
export function useCardSlug(cardId: string) {
  return useQuery({
    queryKey: ['card-slug', cardId],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await db.rpc('get_card_slug', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      return typeof data === 'string' && data ? data : null;
    },
    staleTime: 60 * 60 * 1000,
  });
}

/** Texte de la feuille de partage native. */
export function challengeShareText(share: CardShare, cardTitle: string): string {
  const hora =
    share.hora == null
      ? null
      : share.xp_max != null
        ? `${share.hora} / ${share.xp_max} HORA`
        : `${share.hora} HORA`;
  if (share.mastery != null) {
    return `J'ai ${share.mastery} % de maîtrise et ${hora ?? '0 HORA'} sur « ${cardTitle} ». Tu fais mieux ?`;
  }
  if (hora && share.hora) return `J'ai ${hora} sur « ${cardTitle} ». Tu fais mieux ?`;
  return `Je te défie sur la SKILLCARD « ${cardTitle} » sur JeRaisonne !`;
}

/** Bandeau du visiteur : « Léa te défie : 80 % de maîtrise ». */
export function challengeBannerText(share: Pick<CardShare, 'first_name' | 'mastery'>): string {
  return share.mastery == null
    ? `${share.first_name} te propose cette SKILLCARD`
    : `${share.first_name} te défie : ${share.mastery} % de maîtrise`;
}

export const CIRCLE_RELATION_LABELS: Record<CircleRelation, string> = {
  moi: 'Toi',
  prof: 'Prof',
  parent: 'Parent',
  enfant: 'Enfant',
  eleve: 'Élève',
  camarade: 'Camarade',
};

/** Ma position hors du top affiché (sinon null : déjà dans la liste). */
export function myRowOutsideTop(board: CircleLeaderboard | null | undefined): CircleEntry | null {
  if (!board?.me) return null;
  return board.entries.some((e) => e.is_me) ? null : board.me;
}

// Le jeton survit à la connexion (OTP / OAuth / onboarding) le temps de la session.
const storageKey = (cardId: string) => `jr_card_challenge:${cardId}`;

export function rememberChallengeToken(cardId: string, token: string): void {
  try {
    window.sessionStorage.setItem(storageKey(cardId), token);
  } catch {
    /* stockage indisponible */
  }
}

export function recallChallengeToken(cardId: string): string | null {
  try {
    const v = window.sessionStorage.getItem(storageKey(cardId));
    return isChallengeToken(v) ? v : null;
  } catch {
    return null;
  }
}

// ── Hooks ───────────────────────────────────────────────────────────────────

const SHARE_ERRORS: Record<string, string> = {
  share_rate_limited: 'Tu as beaucoup partagé aujourd’hui : réessaie demain.',
  card_not_published: 'Cette carte n’est pas encore publiée.',
  not_authenticated: 'Connecte-toi pour défier tes amis.',
};

export function translateShareError(message: string): string {
  const key = Object.keys(SHARE_ERRORS).find((k) => message.includes(k));
  return key ? (SHARE_ERRORS[key] as string) : message;
}

export function useCreateCardShare(cardId: string) {
  return useMutation({
    mutationFn: async (): Promise<CardShare> => {
      const { data, error } = await db.rpc('create_card_share', { p_card_id: cardId });
      if (error) throw new Error(translateShareError(error.message));
      return data as CardShare;
    },
  });
}

export function useCardShare(token: string | null) {
  return useQuery({
    queryKey: ['card-share', token],
    enabled: !!token,
    staleTime: Infinity,
    queryFn: async (): Promise<PublicCardShare | null> => {
      const { data, error } = await db.rpc('get_card_share', { p_token: token });
      if (error) throw new Error(error.message);
      return (data as PublicCardShare | null) ?? null;
    },
  });
}

/**
 * Demande de relation créée en relevant un défi (migration 0299) : `peer` =
 * camarade, `student` / `tutor` = lien prof-élève ; `exists` = déjà liés (ou
 * déjà refusé) ; null = rôles sans relation possible.
 */
export type ChallengeRelation = 'peer' | 'student' | 'tutor' | 'exists' | null;

export interface AcceptCardShareResult {
  accepted: boolean;
  relation: ChallengeRelation;
}

export async function acceptCardShare(token: string): Promise<AcceptCardShareResult> {
  const { data, error } = await db.rpc('accept_card_share', { p_token: token });
  if (error) return { accepted: false, relation: null };
  const raw = (data ?? {}) as { accepted?: boolean; relation?: unknown };
  const relation = ['peer', 'student', 'tutor', 'exists'].includes(raw.relation as string)
    ? (raw.relation as ChallengeRelation)
    : null;
  return { accepted: !!raw.accepted, relation };
}

/** Phrase affichée après avoir relevé un défi qui a créé une demande de relation. */
export function challengeRelationText(
  relation: ChallengeRelation,
  firstName: string,
): string | null {
  switch (relation) {
    case 'peer':
      return `Demande envoyée : tu deviendras camarade de ${firstName} dès qu’il ou elle l’aura acceptée.`;
    case 'student':
      return `Demande envoyée : ${firstName} pourra te suivre comme élève dès qu’il ou elle l’aura acceptée.`;
    case 'tutor':
      return `Demande envoyée : tu suivras ${firstName} dès qu’il ou elle l’aura acceptée.`;
    default:
      return null;
  }
}

export const circleQueryKey = (cardId: string) => ['card-circle', cardId] as const;

export function useCircleLeaderboard(cardId: string, enabled: boolean) {
  return useQuery({
    queryKey: circleQueryKey(cardId),
    enabled,
    queryFn: async (): Promise<CircleLeaderboard> => {
      const { data, error } = await db.rpc('get_card_circle_leaderboard', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      const raw = (data ?? {}) as Partial<CircleLeaderboard>;
      return {
        entries: raw.entries ?? [],
        me: raw.me ?? null,
        total: raw.total ?? 0,
        my_visibility: !!raw.my_visibility,
      };
    },
  });
}
